import React, { useCallback, useEffect } from 'react'

import { useRemoteStrings } from '@acorns/web-utils'

import { useBlueprintBindings } from '../utils/blueprint-bindings-context'
import type { GroveBlueprintComponentProps } from '../utils/grove-blueprint-component-props'
import { resolveModifiers } from '../utils/resolve-modifiers'
import { resolveTextValue } from '../utils/resolve-text-value'

export function GroveBlueprintPicker({ node }: GroveBlueprintComponentProps) {
  const { getRemoteString } = useRemoteStrings()
  const { bindingValues, bindingHandlers } = useBlueprintBindings()

  const bindingKey =
    node.type === 'Picker' ? node.props.selection.binding : undefined

  const handleSelect = useCallback(
    (value: string) => {
      if (bindingKey === undefined) {
        return
      }

      const bindingHandler = bindingHandlers[bindingKey]

      if (bindingHandler !== undefined) {
        bindingHandler(value)
      } else {
        // deno-lint-ignore no-console
        console.error(`Missing binding handler for key: "${bindingKey}"`)
      }
    },
    [bindingKey, bindingHandlers],
  )

  useEffect(
    function writeDefaultValueToBindingValues() {
      if (bindingKey === undefined) {
        return
      }

      if (bindingValues[bindingKey] !== undefined) {
        return
      }

      const defaultValue =
        node.type === 'Picker' ? node.props.defaultValue : undefined

      if (defaultValue === undefined) {
        return
      }

      const bindingHandler = bindingHandlers[bindingKey]

      if (bindingHandler !== undefined) {
        bindingHandler(defaultValue)
      }
    },
    [bindingKey, bindingValues, bindingHandlers, node],
  )

  /* v8 ignore if -- @preserve */
  if (node.type !== 'Picker') {
    return null
  }

  const { className, style, attributes } = resolveModifiers(
    node.modifiers,
    bindingValues,
    (key: string) => getRemoteString({ key }),
  )

  const selectedValue = String(
    bindingValues[node.props.selection.binding] ??
      node.props.defaultValue ??
      '',
  )

  const selectedIndex = node.props.options.findIndex(
    (option) => option.value === selectedValue,
  )

  const optionCount = node.props.options.length

  if (node.props.style === 'segmented') {
    return (
      <div
        className={`w-full ${className}`.trim() || undefined}
        style={style}
        {...attributes}
      >
        <fieldset
          className={
            'relative flex w-full flex-row rounded-full bg-acorns-ivory-light p-1'
          }
        >
          <div
            className={
              'absolute top-1 bottom-1 rounded-full bg-white shadow-[0_2px_6px_0_rgba(0,0,0,0.05)] transition-transform duration-200 ease-in-out'
            }
            style={{
              width: `calc((100% - 8px) / ${optionCount})`,
              transform: `translateX(${selectedIndex * 100}%)`,
            }}
          />
          {node.props.options.map((option) => {
            const label = resolveTextValue(
              option.label,
              (key: string) => getRemoteString({ key }),
              bindingValues,
            )

            return (
              <label
                className={`relative flex-1 rounded-full px-3 py-1.5 text-button font-demibold text-center cursor-pointer transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-acorns-slate ${
                  option.value === selectedValue
                    ? 'text-acorns-slate'
                    : 'text-acorns-stone'
                }`}
                key={option.value}
              >
                <input
                  checked={option.value === selectedValue}
                  className={'sr-only'}
                  name={node.props.selection.binding}
                  onChange={() => handleSelect(option.value)}
                  type={'radio'}
                  value={option.value}
                />
                {label}
              </label>
            )
          })}
        </fieldset>
      </div>
    )
  }

  return null
}
